import { useQuery } from '@tanstack/react-query';
import type {
  HomeLocationDto,
  ItemDto,
  LocationDto,
  Page,
  PutawayRuleDto,
  PutawayStrategy,
  PutawayTaskDto,
} from '@zenarty/shared';
import { Forklift, Home, ListOrdered, Plus, ScanLine, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { api } from '../api';
import { useAuth } from '../auth';
import { LpnLabelDialog } from '../components/barcode';
import {
  Badge,
  Button,
  Callout,
  Card,
  EmptyState,
  Field,
  PageHeader,
  Tabs,
} from '../components/kit';
import { Dialog } from '../components/overlays';
import { ItemPicker, LocationPicker } from '../components/pickers';
import { DataTable, type Column } from '../components/table';
import { useToast } from '../components/toast';
import { formatNumber } from '../lib/format';
import { useAction, useClients, useSites } from '../lib/queries';
import { PutawayDialog, PutawayTable } from './ReceiptDetail';

function SiteSelect({
  value,
  onChange,
  all = true,
}: {
  value: string;
  onChange: (v: string) => void;
  all?: boolean;
}) {
  const sites = useSites();
  return (
    <select
      className="select"
      aria-label="Site"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {all ? <option value="">All sites</option> : <option value="">Choose a site…</option>}
      {(sites.data?.items ?? []).map((s) => (
        <option key={s.id} value={s.id}>
          {s.name}
        </option>
      ))}
    </select>
  );
}

export function PutawayPage() {
  const { can } = useAuth();
  const toast = useToast();
  const [siteId, setSiteId] = useState('');
  const [state, setState] = useState<'open' | 'done'>('open');
  const [task, setTask] = useState<PutawayTaskDto | null>(null);
  const [label, setLabel] = useState<string | null>(null);
  const [scan, setScan] = useState('');
  const list = useQuery({
    queryKey: ['putaway', siteId, state],
    queryFn: () =>
      api<Page<PutawayTaskDto>>(
        `/putaway-tasks?state=${state}&limit=200${siteId ? `&siteId=${siteId}` : ''}`,
      ),
    refetchInterval: 20_000,
  });
  const open = async (e: FormEvent) => {
    e.preventDefault();
    try {
      setTask(
        await api<PutawayTaskDto>(
          `/putaway-tasks/lpn/${encodeURIComponent(scan.trim().toUpperCase())}`,
        ),
      );
      setScan('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Not found');
    }
  };
  const tasks = list.data?.items ?? [];
  const unsuggested = tasks.filter((t) => t.state === 'open' && !t.suggestedLocationId).length;
  return (
    <div className="stack-lg">
      <PageHeader
        title="Putaway"
        description="Directed putaway: scan the LPN, go to the suggested location, scan it to confirm. Overrides need a reason; placement rules always apply."
        actions={<SiteSelect value={siteId} onChange={setSiteId} />}
      />
      {can('putaway:execute') ? (
        <Card>
          <form
            onSubmit={(e) => void open(e)}
            className="row"
            style={{ gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}
          >
            <Field label="Scan an LPN">
              <input
                className="input mono"
                style={{ minWidth: 260 }}
                value={scan}
                onChange={(e) => setScan(e.target.value)}
                placeholder="LPN or SSCC"
              />
            </Field>
            <Button type="submit" icon={ScanLine} disabled={!scan.trim()}>
              Open task
            </Button>
          </form>
        </Card>
      ) : null}
      {unsuggested ? (
        <Callout tone="warn" title={`${unsuggested} LPN(s) have no compatible location`}>
          Every candidate failed a placement rule. Free up space, add locations, or adjust the
          putaway rules — then use “Suggest again”.
        </Callout>
      ) : null}
      <div>
        <Tabs
          value={state}
          onChange={setState}
          tabs={[
            {
              id: 'open',
              label: 'Waiting',
              count: state === 'open' ? list.data?.total : undefined,
            },
            { id: 'done', label: 'Completed' },
          ]}
        />
        <PutawayTable
          tasks={tasks}
          onConfirm={can('putaway:execute') ? setTask : undefined}
          onLabel={(t) => setLabel(t.lpnId)}
        />
      </div>
      {task ? <PutawayDialog task={task} onClose={() => setTask(null)} /> : null}
      {label ? <LpnLabelDialog lpnId={label} onClose={() => setLabel(null)} /> : null}
    </div>
  );
}

const STRATEGY: Record<PutawayStrategy, { label: string; help: string }> = {
  fixed_home: { label: 'Fixed home', help: "The item's home location(s) at this site" },
  consolidate: { label: 'Consolidate', help: 'Locations already holding the SKU (same lot first)' },
  zone: { label: 'Zone', help: 'Any compatible location in one zone (e.g. A-velocity zone)' },
  nearest_empty: { label: 'Nearest empty', help: 'Closest empty compatible location to the dock' },
};

export function PutawayRulesPage() {
  const { can } = useAuth();
  const [tab, setTab] = useState<'rules' | 'homes'>('rules');
  const [siteId, setSiteId] = useState('');
  return (
    <div className="stack-lg">
      <PageHeader
        title="Putaway rules"
        description="Rules are tried in priority order; the first compatible location wins. With no rules, the engine uses fixed home → consolidate → nearest empty."
        actions={<SiteSelect value={siteId} onChange={setSiteId} />}
      />
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'rules', label: 'Rule chain' },
          { id: 'homes', label: 'Home locations' },
        ]}
      />
      {tab === 'rules' ? (
        <Rules siteId={siteId} canEdit={can('config:manage')} />
      ) : (
        <Homes siteId={siteId} canEdit={can('location:manage')} />
      )}
    </div>
  );
}

function Rules({ siteId, canEdit }: { siteId: string; canEdit: boolean }) {
  const sites = useSites();
  const siteName = new Map((sites.data?.items ?? []).map((s) => [s.id, s.name]));
  const [editing, setEditing] = useState<PutawayRuleDto | 'new' | null>(null);
  const q = useQuery({
    queryKey: ['putaway-rules', siteId],
    queryFn: () => api<PutawayRuleDto[]>(`/putaway-rules${siteId ? `?siteId=${siteId}` : ''}`),
  });
  const del = useAction((id: string) => api(`/putaway-rules/${id}`, { method: 'DELETE' }), {
    invalidate: [['putaway-rules']],
    success: 'Rule removed',
  });
  const columns: Column<PutawayRuleDto>[] = [
    {
      id: 'p',
      header: 'Priority',
      width: 80,
      cell: (r) => <span className="num">{r.priority}</span>,
    },
    { id: 'site', header: 'Site', cell: (r) => siteName.get(r.siteId) ?? '—' },
    {
      id: 'when',
      header: 'Applies to',
      cell: (r) => {
        const conds = [
          r.clientName,
          r.storageClass ? `${r.storageClass} storage` : null,
          r.temperatureClass,
          r.velocityClass ? `${r.velocityClass}-velocity` : null,
        ].filter(Boolean);
        return conds.length ? conds.join(' · ') : <span className="muted">Everything</span>;
      },
    },
    {
      id: 'strategy',
      header: 'Strategy',
      cell: (r) => (
        <div>
          <div className="primary-cell">
            {STRATEGY[r.strategy].label}
            {r.zone ? ` ${r.zone}` : ''}
            {r.locationType ? (
              <span className="muted"> · {r.locationType.replace('_', ' ')}</span>
            ) : null}
          </div>
          <div className="sub">{STRATEGY[r.strategy].help}</div>
        </div>
      ),
    },
    {
      id: 'active',
      header: 'Status',
      cell: (r) => (
        <Badge tone={r.active ? 'ok' : 'neutral'} dot>
          {r.active ? 'Active' : 'Off'}
        </Badge>
      ),
    },
    ...(canEdit
      ? [
          {
            id: 'act',
            header: '',
            align: 'right' as const,
            cell: (r: PutawayRuleDto) => (
              <Button
                size="sm"
                variant="ghost"
                icon={Trash2}
                aria-label="Delete rule"
                onClick={() => del.mutate(r.id)}
              />
            ),
          },
        ]
      : []),
  ];
  return (
    <>
      <DataTable
        columns={columns}
        rows={q.data ?? []}
        rowKey={(r) => r.id}
        loading={q.isLoading}
        onRowClick={canEdit ? setEditing : undefined}
        toolbar={
          canEdit ? (
            <Button variant="primary" icon={Plus} onClick={() => setEditing('new')}>
              Add rule
            </Button>
          ) : null
        }
        empty={
          <EmptyState icon={ListOrdered} title="Using the default chain">
            Fixed home → consolidate with the same SKU → nearest empty location. Add rules to direct
            velocity classes, temperatures or clients to zones.
          </EmptyState>
        }
      />
      {editing ? (
        <RuleDialog
          rule={editing === 'new' ? null : editing}
          defaultSite={siteId}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}

function RuleDialog({
  rule,
  defaultSite,
  onClose,
}: {
  rule: PutawayRuleDto | null;
  defaultSite: string;
  onClose: () => void;
}) {
  const sites = useSites();
  const clients = useClients();
  const [v, setV] = useState({
    siteId: rule?.siteId ?? defaultSite,
    clientId: rule?.clientId ?? '',
    storageClass: rule?.storageClass ?? '',
    temperatureClass: rule?.temperatureClass ?? '',
    velocityClass: rule?.velocityClass ?? '',
    strategy: rule?.strategy ?? 'zone',
    zone: rule?.zone ?? '',
    locationType: rule?.locationType ?? '',
    priority: String(rule?.priority ?? 100),
    active: rule?.active ?? true,
  });
  const set = (patch: Partial<typeof v>) => setV((x) => ({ ...x, ...patch }));
  const save = useAction(
    () => {
      const body = {
        siteId: v.siteId,
        clientId: v.clientId || null,
        storageClass: v.storageClass || null,
        temperatureClass: v.temperatureClass || null,
        velocityClass: v.velocityClass || null,
        strategy: v.strategy,
        zone: v.strategy === 'zone' ? v.zone : null,
        locationType: v.locationType || null,
        priority: Number(v.priority),
        active: v.active,
      };
      return rule
        ? api(`/putaway-rules/${rule.id}`, { method: 'PATCH', body })
        : api('/putaway-rules', { method: 'POST', body });
    },
    { invalidate: [['putaway-rules']], success: 'Rule saved', onSuccess: onClose },
  );
  return (
    <Dialog
      title={rule ? 'Edit putaway rule' : 'New putaway rule'}
      description="Conditions narrow which loads the rule applies to; empty means any."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!v.siteId || (v.strategy === 'zone' && !v.zone)}
            onClick={() => save.mutate(undefined)}
          >
            Save rule
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Site">
          <select
            className="select"
            value={v.siteId}
            onChange={(e) => set({ siteId: e.target.value })}
          >
            <option value="">Choose…</option>
            {(sites.data?.items ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Priority" hint="Lower runs first">
          <input
            className="input num"
            value={v.priority}
            onChange={(e) => set({ priority: e.target.value })}
          />
        </Field>
        <Field label="Strategy" className="span-2">
          <select
            className="select"
            value={v.strategy}
            onChange={(e) => set({ strategy: e.target.value as PutawayStrategy })}
          >
            {(Object.keys(STRATEGY) as PutawayStrategy[]).map((k) => (
              <option key={k} value={k}>
                {STRATEGY[k].label} — {STRATEGY[k].help}
              </option>
            ))}
          </select>
        </Field>
        {v.strategy === 'zone' ? (
          <Field label="Zone">
            <input
              className="input mono"
              value={v.zone}
              onChange={(e) => set({ zone: e.target.value.toUpperCase() })}
            />
          </Field>
        ) : null}
        <Field label="Location type">
          <select
            className="select"
            value={v.locationType}
            onChange={(e) => set({ locationType: e.target.value })}
          >
            <option value="">Any storage</option>
            {['rack', 'shelving', 'floor', 'flow_rack'].map((t) => (
              <option key={t} value={t}>
                {t.replace('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Client">
          <select
            className="select"
            value={v.clientId}
            onChange={(e) => set({ clientId: e.target.value })}
          >
            <option value="">Any</option>
            {(clients.data?.items ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Temperature">
          <select
            className="select"
            value={v.temperatureClass}
            onChange={(e) => set({ temperatureClass: e.target.value })}
          >
            <option value="">Any</option>
            <option value="ambient">Ambient</option>
            <option value="chilled">Chilled</option>
            <option value="frozen">Frozen</option>
          </select>
        </Field>
        <Field label="Velocity class" hint="A = fastest 80% of units moved (90 days)">
          <select
            className="select"
            value={v.velocityClass}
            onChange={(e) => set({ velocityClass: e.target.value })}
          >
            <option value="">Any</option>
            <option value="A">A</option>
            <option value="B">B</option>
            <option value="C">C</option>
          </select>
        </Field>
        <Field label="Storage class">
          <input
            className="input"
            value={v.storageClass}
            placeholder="Any"
            onChange={(e) => set({ storageClass: e.target.value })}
          />
        </Field>
        <label className="check span-2">
          <input
            type="checkbox"
            checked={v.active}
            onChange={(e) => set({ active: e.target.checked })}
          />{' '}
          Active
        </label>
      </div>
    </Dialog>
  );
}

function Homes({ siteId, canEdit }: { siteId: string; canEdit: boolean }) {
  const [adding, setAdding] = useState(false);
  const q = useQuery({
    queryKey: ['home-locations', siteId],
    queryFn: () => api<HomeLocationDto[]>(`/home-locations${siteId ? `?siteId=${siteId}` : ''}`),
  });
  const del = useAction((id: string) => api(`/home-locations/${id}`, { method: 'DELETE' }), {
    invalidate: [['home-locations']],
    success: 'Home location removed',
  });
  const columns: Column<HomeLocationDto>[] = [
    { id: 'loc', header: 'Location', cell: (h) => <span className="mono">{h.locationCode}</span> },
    {
      id: 'item',
      header: 'Item',
      cell: (h) => (
        <div>
          <div className="primary-cell">{h.description}</div>
          <div className="sub mono">{h.sku}</div>
        </div>
      ),
    },
    {
      id: 'minmax',
      header: 'Min / max',
      cell: (h) => (
        <span className="num">
          {formatNumber(h.minQty)} / {formatNumber(h.maxQty)}
        </span>
      ),
    },
    {
      id: 'onhand',
      header: 'On hand',
      align: 'right',
      cell: (h) => (
        <Badge tone={h.onHand <= h.minQty ? 'warn' : 'ok'}>{formatNumber(h.onHand)}</Badge>
      ),
    },
    ...(canEdit
      ? [
          {
            id: 'act',
            header: '',
            align: 'right' as const,
            cell: (h: HomeLocationDto) => (
              <Button
                size="sm"
                variant="ghost"
                icon={Trash2}
                aria-label="Remove"
                onClick={() => del.mutate(h.id)}
              />
            ),
          },
        ]
      : []),
  ];
  return (
    <>
      <DataTable
        columns={columns}
        rows={q.data ?? []}
        rowKey={(h) => h.id}
        loading={q.isLoading}
        searchText={(h) => `${h.sku} ${h.description} ${h.locationCode}`}
        searchPlaceholder="Search SKU or location"
        toolbar={
          canEdit ? (
            <Button variant="primary" icon={Plus} onClick={() => setAdding(true)}>
              Assign home
            </Button>
          ) : null
        }
        empty={
          <EmptyState icon={Home} title="No home locations">
            Give fast movers a fixed pick face: putaway fills it first and replenishment tops it up
            to max.
          </EmptyState>
        }
      />
      {adding ? <HomeDialog defaultSite={siteId} onClose={() => setAdding(false)} /> : null}
    </>
  );
}

function HomeDialog({ defaultSite, onClose }: { defaultSite: string; onClose: () => void }) {
  const [siteId, setSiteId] = useState(defaultSite);
  const [item, setItem] = useState<ItemDto | null>(null);
  const [loc, setLoc] = useState<LocationDto | null>(null);
  const [min, setMin] = useState('0');
  const [max, setMax] = useState('0');
  const save = useAction(
    () =>
      api('/home-locations', {
        method: 'POST',
        body: {
          siteId,
          itemId: item?.id,
          locationId: loc?.id,
          minQty: Number(min),
          maxQty: Number(max),
        },
      }),
    { invalidate: [['home-locations']], success: 'Home location assigned', onSuccess: onClose },
  );
  return (
    <Dialog
      title="Assign a home location"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            icon={Forklift}
            loading={save.isPending}
            disabled={!siteId || !item || !loc}
            onClick={() => save.mutate(undefined)}
          >
            Assign
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Site" className="span-2">
          <SiteSelect value={siteId} onChange={setSiteId} all={false} />
        </Field>
        <Field label="Item" className="span-2">
          <ItemPicker value={item} onChange={setItem} />
        </Field>
        <Field label="Location" className="span-2">
          {siteId ? (
            <LocationPicker siteId={siteId} value={loc} onChange={setLoc} />
          ) : (
            <span className="muted small">Choose a site first</span>
          )}
        </Field>
        <Field label="Min (replenish below)">
          <input className="input num" value={min} onChange={(e) => setMin(e.target.value)} />
        </Field>
        <Field label="Max (fill up to)">
          <input className="input num" value={max} onChange={(e) => setMax(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
